/**
 * Quem foi atendido e não voltou — a regra, sem banco.
 *
 * **Uma implementação só.** Havia duas: a lista da tela ignorava agendamento
 * cancelado, e a contagem que alimentava a automação e o marketing
 * não. Um cancelamento recente virava "última visita", o paciente saía da
 * contagem e ficava na lista — a tela dizia 1 e a contagem dizia 0 para a mesma
 * pergunta. Agora as duas saem daqui.
 */

import type { RetornoEmAberto } from './tipos';

const FUSO = 'America/Sao_Paulo';

/**
 * O que não é visita nem compromisso: cancelado não aconteceu, e bloqueio é
 * a agenda do profissional, não do paciente.
 */
const NAO_CONTA = new Set(['cancelado', 'bloqueio']);

export interface AgendamentoParaRetorno {
    contatoId: string;
    /** Instante do agendamento, ISO. */
    inicioEm: string;
    status: string;
    nome?: string;
    telefone?: string;
    procedimento?: string;
}

function meioDiaEm(dia: string): number {
    return new Date(`${dia}T12:00:00-03:00`).getTime();
}

function diaNoFuso(ms: number): string {
    return new Date(ms).toLocaleDateString('en-CA', { timeZone: FUSO });
}

export function retornosEmAbertoDe(agendamentos: AgendamentoParaRetorno[], dias: number, agora: Date): RetornoEmAberto[] {
    const instante = agora.getTime();
    const hoje = diaNoFuso(instante);
    const corte = diaNoFuso(meioDiaEm(hoje) - dias * 86400000);

    const comFuturo = new Set<string>();
    const ultimo = new Map<string, AgendamentoParaRetorno>();

    for (const a of agendamentos) {
        if (NAO_CONTA.has(a.status)) continue;
        if (new Date(a.inicioEm).getTime() >= instante) {
            comFuturo.add(a.contatoId);
            continue;
        }
        const atual = ultimo.get(a.contatoId);
        if (!atual || atual.inicioEm < a.inicioEm) ultimo.set(a.contatoId, a);
    }

    return [...ultimo.values()]
        .filter((a) => !comFuturo.has(a.contatoId) && diaNoFuso(new Date(a.inicioEm).getTime()) < corte)
        .map((a) => {
            const dia = diaNoFuso(new Date(a.inicioEm).getTime());
            return {
                contatoId: a.contatoId,
                nome: a.nome ?? '—',
                telefone: a.telefone ?? '',
                ultimoAtendimentoEm: dia,
                diasSemRetorno: Math.round((meioDiaEm(hoje) - meioDiaEm(dia)) / 86400000),
                ultimoProcedimento: a.procedimento ?? '—',
            };
        })
        .sort((a, b) => b.diasSemRetorno - a.diasSemRetorno);
}
